import dayjs from 'dayjs';

import {
  getStartEndTimeOfMultDays,
  useAutoSuggestions,
  useAuthContext,
  useSyncSleepDetails,
  wearableErrorSnackBarAtom,
  failedSyncTerraUserIdsAtom,
  ErrorRes,
  SyncErrorRes,
} from '../data-access';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useRecoilState } from 'recoil';

interface SyncSleepDataParams {
  terraUserIds: string[];
  startDate: string;
  endDate: string;
}

export const useSyncSleepData = () => {
  const { syncSleepDetail, error: syncSleepDetailError } = useSyncSleepDetails();
  const { user } = useAuthContext();
  const [_, setWearableErrorSnackbar] = useRecoilState(wearableErrorSnackBarAtom);
  const [, setFailedSyncTerraUserIds] = useRecoilState(failedSyncTerraUserIdsAtom);
  const [hasSomeDataSynched, setHasSomeDataSynched] = useState(false);

  useEffect(() => {
    if (!syncSleepDetailError) {
      return;
    }

    const error = (syncSleepDetailError as AxiosError).response;
    const syncErrorData = error?.data as SyncErrorRes | undefined;
    const errorMessage = syncErrorData?.error;

    if (error?.status === 401) {
      if (syncErrorData?.terraUserId) {
        setFailedSyncTerraUserIds((prev) => Array.from(new Set([...prev, syncErrorData.terraUserId])));
      }
      setWearableErrorSnackbar({
        message: `Device connection issue.${errorMessage ? ` ${errorMessage}` : ''}`,
        status: 401,
      });
      return;
    }

    setWearableErrorSnackbar({
      message: `Sleep sync didn't go through.${errorMessage ? ` ${errorMessage}` : ''}`,
      status: error?.status,
    });
  }, [setWearableErrorSnackbar, syncSleepDetailError]);

  const syncSleepData = async ({ terraUserIds, startDate, endDate }: SyncSleepDataParams) => {
    if (!user.metricId || !user.userId || !terraUserIds.length) {
      return;
    }
    setHasSomeDataSynched(false);

    const { startTime, endTime } = getStartEndTimeOfMultDays(dayjs(startDate), dayjs(endDate));
    if (terraUserIds.length) {
      try {
        const results = await syncSleepDetail({
          userId: user.userId,
          metricId: user.metricId,
          terraUserIds,
          startDate: startTime.toISOString(),
          endDate: endTime.toISOString(),
        });
        if (results.length) {
          setHasSomeDataSynched(true);
        }
      } catch (error) {
        console.error('Error syncing sleep data', error);
      }
    }
  };

  return { hasSomeDataSynched, syncSleepData };
};
